import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import { leaveAPI } from '../services/api';

interface DetailedBalance {
  employee_name: string;
  year: number;
  ferie_totali: number;
  ferie_usate: number;
  ferie_residue: number;
  ferie_anteriori: number;
  permessi_totali: number;
  permessi_usate: number;
  permessi_residue: number;
  ex_festivita_ore: number;
  giorni_malattia: number;
}

const DetailedLeaveBalance: React.FC = () => {
  const [balance, setBalance] = useState<DetailedBalance | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async () => {
    try {
      const data = await leaveAPI.getDetailedBalance();
      setBalance(data);
    } catch (error) {
      console.error('Errore nel caricamento del saldo:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!balance) {
    return (
      <Alert severity="info">
        Nessun saldo disponibile per l'anno corrente.
      </Alert>
    );
  }

  const ferieProgress = balance.ferie_totali > 0
    ? (balance.ferie_usate / balance.ferie_totali) * 100
    : 0;

  const permessiProgress = balance.permessi_totali > 0
    ? (balance.permessi_usate / balance.permessi_totali) * 100
    : 0;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
        Il Mio Saldo Ferie e Permessi
      </Typography>

      <Grid container spacing={3}>
        {/* Card Ferie */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderLeft: '4px solid #4caf50', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Ferie Residue
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 'bold', color: '#4caf50', mb: 1 }}
              >
                {Math.round(balance.ferie_residue)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                giorni disponibili
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Totali anno:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {Math.round(balance.ferie_totali)} gg
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Usate:</Typography>
                <Typography variant="body2" color="error.main">
                  {Math.round(balance.ferie_usate)} gg
                </Typography>
              </Box>
              {balance.ferie_anteriori > 0 && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Da anni precedenti:</Typography>
                  <Typography variant="body2" color="primary.main">
                    {Math.round(balance.ferie_anteriori)} gg
                  </Typography>
                </Box>
              )}

              <LinearProgress
                variant="determinate"
                value={Math.min(ferieProgress, 100)}
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0f2f1',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: ferieProgress > 100 ? '#f44336' : '#4caf50',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Utilizzo: {ferieProgress.toFixed(0)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card Permessi */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderLeft: '4px solid #2196f3', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Permessi Residui
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 'bold', color: '#2196f3', mb: 1 }}
              >
                {Math.round(balance.permessi_residue)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ore disponibili
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Totali anno:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {Math.round(balance.permessi_totali)} h
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Usati:</Typography>
                <Typography variant="body2" color="error.main">
                  {Math.round(balance.permessi_usate)} h
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={Math.min(permessiProgress, 100)}
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e3f2fd',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: permessiProgress > 100 ? '#f44336' : '#2196f3',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Utilizzo: {permessiProgress.toFixed(0)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card Ex Festività */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderLeft: '4px solid #9c27b0', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Ex Festività
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 1 }}
              >
                {Math.round(balance.ex_festivita_ore)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ore disponibili
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">
                Ore di ex festività maturate e non utilizzate.
                Queste ore possono essere utilizzate come permessi retribuiti.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card Malattia */}
        <Grid item xs={12}>
          <Card sx={{ borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Giorni di Malattia Utilizzati
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 'bold', color: '#ff9800' }}
                  >
                    {Math.round(balance.giorni_malattia)} giorni
                  </Typography>
                </Box>
                <Chip
                  label={`Anno ${balance.year}`}
                  color="default"
                  size="medium"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Numero di giorni di malattia usufruiti nell'anno corrente (dato puramente informativo).
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alert per saldi negativi */}
      {balance.ferie_residue < 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <strong>Attenzione:</strong> Il saldo ferie è negativo (
          {Math.round(balance.ferie_residue)} giorni). Contatta l'amministrazione per
          regolarizzare la situazione.
        </Alert>
      )}

      {balance.permessi_residue < 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <strong>Attenzione:</strong> Il saldo permessi è negativo (
          {Math.round(balance.permessi_residue)} ore). Contatta l'amministrazione per
          regolarizzare la situazione.
        </Alert>
      )}
    </Box>
  );
};

export default DetailedLeaveBalance;
